import React, { useState } from "react";
import {
  FaChalkboardTeacher,
  FaBook,
  FaBus,
  FaFutbol,
  FaFlask,
  FaLaptop,
  FaBullseye,
  FaEye,
  FaTrophy,
  FaMedal,
  FaCertificate,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import CountUp from "react-countup";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./About.css";
import { Link } from "react-router-dom";

export default function About() {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="about-page">
      {/* Hero Section */}

      <section className="hero-section fade-in">
        <div className="hero-content">
          <h1 className="hero-title">Welcome to Our Institution</h1>
          <strong>
            <p className="hero-subtitle">
              A place where education meets values, and every student discovers
              their true potential.
            </p>
          </strong>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section slide-up">
        <h2>Who We Are</h2>
        <p>
          Our school is committed to providing holistic education by combining
          academics, sports, and extracurricular activities to prepare students
          for a brighter future.
        </p>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision fade-in">
        <div className="card">
          <FaBullseye className="icon" />
          <h3>Our Mission</h3>
          <p>
            To empower students with knowledge and skills to excel academically
            and socially.
          </p>
        </div>
        <div className="card">
          <FaEye className="icon" />
          <h3>Our Vision</h3>
          <p>
            To create responsible global citizens with strong values and a
            lifelong passion for learning.
          </p>
        </div>
      </section>

      {/* Principal's Message */}
      <section className="principal-section">
        <h2>Director Message</h2>
        <div className="principal-container">
          <div className="principal-image">
            <img src="/images/Director.webp" alt="Director" />
          </div>
          <div className="principal-text">
            <p>
              Welcome to our school, a place where we nurture creativity,
              knowledge, and excellence. Our goal is to inspire students to
              achieve their full potential and contribute positively to society.
              We believe in creating an inclusive, safe, and supportive
              environment for all.
            </p>
            <h3>- Mr Nadeem Shahzad</h3>
            <p className="principal-role">Director</p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="choose-section fade-in">
        <h2>Why Choose Our School?</h2>
        <div className="choose-grid">
          <div className="choose-card zoom-in">
            <FaChalkboardTeacher className="icon" />
            <h3>Expert Faculty</h3>
            <p>
              Highly qualified teachers dedicated to student growth and success.
            </p>
          </div>
          <div className="choose-card zoom-in">
            <FaLaptop className="icon" />
            <h3>Modern Learning</h3>
            <p>
              Smart classrooms, digital resources, and innovative teaching
              methods.
            </p>
          </div>
          <div className="choose-card zoom-in">
            <FaFutbol className="icon" />
            <h3>Beyond Academics</h3>
            <p>Strong focus on sports, arts, and extracurricular excellence.</p>
          </div>
          <div className="choose-card zoom-in">
            <FaUsers className="icon" />
            <h3>Safe & Supportive</h3>
            <p>
              A nurturing and secure environment for holistic child development.
            </p>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="facilities-section slide-up">
        <h2>Our Facilities</h2>
        <div className="facilities-grid">
          <div className="facility-card">
            <FaBook className="icon" />
            <h3>Library</h3>
          </div>
          <div className="facility-card">
            <FaFlask className="icon" />
            <h3>Science Labs</h3>
          </div>
          <div className="facility-card">
            <FaLaptop className="icon" />
            <h3>Computer Labs</h3>
          </div>
          <div className="facility-card">
            <FaFutbol className="icon" />
            <h3>Sports</h3>
          </div>
          <div className="facility-card">
            <FaBus className="icon" />
            <h3>Transport</h3>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="achievements-section slide-up">
        <h2>Our Achievements</h2>
        <div className="achievements-grid">
          <div className="achievement-card zoom-in">
            <FaTrophy className="icon" />
            <h3>Best School Award</h3>
            <p>
              Recognized as one of the top institutions for academic excellence.
            </p>
          </div>
          <div className="achievement-card zoom-in">
            <FaMedal className="icon" />
            <h3>Sports Excellence</h3>
            <p>
              Our students consistently win district & national level
              competitions.
            </p>
          </div>
          <div className="achievement-card zoom-in">
            <FaCertificate className="icon" />
            <h3>Academic Excellence</h3>
            <p>
              Top results and scholarship holders in board & international
              exams.
            </p>
          </div>
          <div className="achievement-card zoom-in">
            <FaUserGraduate className="icon" />
            <h3>Successful Alumni</h3>
            <p>Thousands of graduates excelling in their careers worldwide.</p>
          </div>
        </div>
      </section>

      {/* Admission Banner */}
      <section
        className="admission-banner-section"
        aria-label="Admissions Open 2025"
      >
        <motion.div
          className="admission-banner"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="admission-heading">
            Admissions Open 2025 – Enroll Now
          </h2>
          <img
            src="/images/banner.webp"
            alt="Admissions Open Banner"
            className="admission-image"
            loading="lazy"
          />
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="stats-section slide-up">
        {[
          { icon: <FaUsers className="icon" />, end: 1500, label: "Students" },
          {
            icon: <FaChalkboardTeacher className="icon" />,
            end: 80,
            label: "Teachers",
          },
          {
            icon: <FaUserGraduate className="icon" />,
            end: 5000,
            label: "Alumni",
          },
          { icon: <FaTrophy className="icon" />, end: 120, label: "Awards" },
        ].map((stat, i) => (
          <div key={i} className="stat-card zoom-in">
            {stat.icon}
            <h3>
              <CountUp
                end={stat.end}
                duration={3}
                enableScrollSpy
                scrollSpyDelay={200}
              />
              +
            </h3>
            <p>{stat.label}</p>
          </div>
        ))}
      </section>

      {/* Teachers Section */}
      <section className="teachers-section fade-in">
        <h2>Meet Our Team</h2>
        <div className="teachers-grid">
          {[
            "Director.webp",
            "Samreenn.webp",
            "Shana.webp",
            "Aneesa.webp",
            "umer.webp",
            "staff.webp",
          ].map((img, i) => (
            <div className="teacher-card" key={i}>
              <img src={`/images/${img}`} alt="Teacher" />
              <h3>
                {
                  [
                    "Mr Nadeem Shahzad",
                    "Ms Samreen Kousar",
                    "Ms Shahana ",
                    "Ms Anisa ",
                    "Mr Umer Farooq",
                    "Staff",
                  ][i]
                }
              </h3>
              <p>
                {
                  [
                    "Director",
                    "Main Branch Pincipal",
                    "Rangilpur Branch Principal",
                    "Boys Branch Principal",
                    "Head of 9th & 10th",
                    "School Staff",
                  ][i]
                }
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section fade-in">
        <h2>Our Pride Alumni</h2>
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={30}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          breakpoints={{
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
        >
          {[
            {
              name: "Shifa Fatima",
              role: "Avitor",
              img: "images/Shifa.webp",
              feedback:
                "Thanks to my school’s teaching, I gained strong English skills and reached my current position..",
            },

            {
              name: "Aneela Saad",
              role: " Nurse",
              img: "images/aneela.webp",
              feedback:
                "Proud academician shaped by a school of excellence. Its students achieve success.",
            },
            {
              name: "Vaneeza Safder",
              role: "Designer",
              img: "images/Vaneeza.webp",
              feedback:
                "Academic Group of School nurtured my skills and dedication, earning me a design scholarship.",
            },
              {
              name: "H Muhammad Uzair",
              role: "Civil Engineer ",
              img: "images/Uzair.webp",
              feedback:
                "My school built the foundation and values guiding my Civil Engineering journey.",
            },
            {
              name: "Sohaib Safder",
              role: " Doctor",
              img: "images/Sohaib.webp",
              feedback:
                "Teachers are supportive and the learning environment is amazing. I love being part of this school.",
            },
          
          ].map((testimonial, i) => (
            <SwiperSlide key={i}>
              <div className="testimonial-card zoom-in">
                <img
                  src={testimonial.img}
                  alt={testimonial.name}
                  className="testimonial-img"
                />
                <h3>{testimonial.name}</h3>
                <p className="role">{testimonial.role}</p>
                <p className="feedback">"{testimonial.feedback}"</p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* FAQ Section */}
      <section className="faq-section fade-in">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-container">
          {[
            {
              question: "What are the admission requirements?",
              answer:
                "Admission is based on an entrance test, previous academic records, and an interview with the student and parents.",
            },
            {
              question: "Do you offer transportation facilities?",
              answer:
                "Yes, we provide safe and reliable school bus services covering major areas of the city.",
            },
            {
              question: "What extracurricular activities are available?",
              answer:
                "We offer a wide range of activities including sports, music, art, science clubs, and debate competitions.",
            },
            {
              question: "How do you ensure student safety?",
              answer:
                "We have CCTV monitoring, trained security staff, and strict protocols to ensure the safety of every child.",
            },
            {
              question: "What is the fee structure?",
              answer:
                "Our fee structure is designed to be affordable while maintaining high educational standards. Please contact the admission office for detailed information.",
            },
          ].map((faq, i) => (
            <div
              key={i}
              className={`faq-item ${activeIndex === i ? "active" : ""}`}
              onClick={() => toggleFAQ(i)}
            >
              <div className="faq-question">
                <h3>{faq.question}</h3>
                <span>{activeIndex === i ? "−" : "+"}</span>
              </div>
              <div
                className="faq-answer"
                style={{ maxHeight: activeIndex === i ? "200px" : "0" }}
              >
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section slide-up">
        <h2>Join Our School Today!</h2>
        <p>
          A hub of academic excellence, nurturing young minds with innovation,
          values, and lifelong learning for a brighter future.
        </p>
        <Link to="/admissions">
          <button className="cta-button">Apply for Admission</button>
        </Link>
      </section>
    </div>
  );
}
